import type {
  KidTemplateAffectedShareClass,
  KidTemplateIncompleteEntry,
} from '../../../../../api/kids/types';

export function isSameKidDocument(
  overview: Pick<
    KidTemplateAffectedShareClass,
    'fund_id' | 'share_class_id' | 'document_language'
  >,
  fundId: string,
  shareClassId: string,
  documentLanguage: string,
): boolean {
  return (
    overview.fund_id === fundId &&
    overview.share_class_id === shareClassId &&
    overview.document_language === documentLanguage
  );
}

/**
 * Template ids whose `invalid_overviews` include this document. These blocks are
 * present in the layout but invalid/inconsistent and should be removed.
 */
export function getInvalidTemplateIdsForDocument(
  incompleteDocuments: KidTemplateIncompleteEntry[] | undefined,
  fundId: string,
  shareClassId: string,
  documentLanguage: string,
): Set<string> {
  const ids = new Set<string>();

  incompleteDocuments?.forEach((entry) => {
    const isInvalid = entry.invalid_overviews.some((overview) =>
      isSameKidDocument(overview, fundId, shareClassId, documentLanguage),
    );

    if (isInvalid && entry.template_id) {
      ids.add(entry.template_id);
    }
  });

  return ids;
}

/** Whether this document has missing or invalid templates per incomplete-documents API. */
export function isKidDocumentTemplateIncomplete(
  incompleteDocuments: KidTemplateIncompleteEntry[] | undefined,
  fundId: string,
  shareClassId: string,
  documentLanguage: string,
): boolean {
  if (!incompleteDocuments?.length) return false;

  return incompleteDocuments.some(
    (entry) =>
      entry.invalid_overviews.some((overview) =>
        isSameKidDocument(overview, fundId, shareClassId, documentLanguage),
      ) ||
      entry.missing_overviews.some((overview) =>
        isSameKidDocument(overview, fundId, shareClassId, documentLanguage),
      ),
  );
}

export function getMissingTemplateEntriesForDocument(
  incompleteDocuments: KidTemplateIncompleteEntry[] | undefined,
  fundId: string,
  shareClassId: string,
  documentLanguage: string,
): KidTemplateIncompleteEntry[] {
  if (!incompleteDocuments?.length) return [];

  return incompleteDocuments.filter((entry) =>
    entry.missing_overviews.some((overview) =>
      isSameKidDocument(overview, fundId, shareClassId, documentLanguage),
    ),
  );
}

export function hasTemplateIdInKidData(
  kidData: Array<{ template_id?: string | null }> | undefined,
): boolean {
  return (kidData ?? []).some((item) => Boolean(item.template_id));
}

export function hasTemplateIdInElements(
  elements: Array<{ template_id?: string | null }> | undefined,
): boolean {
  return (elements ?? []).some((element) => Boolean(element.template_id));
}

/** Whether review/publish should be blocked for this share class (overview flag). */
export function isShareClassBlockedByTemplateIncomplete(shareClass: {
  has_shareclass_out_of_sync?: boolean;
}): boolean {
  return Boolean(shareClass.has_shareclass_out_of_sync);
}

export const TEMPLATE_INCOMPLETE_ACTION_TOOLTIP =
  'Resolve template issues before reviewing or publishing';
